// /maintenance (board system/maintenance): scheduled maintenance does not stop
// work — the device continues with the local queue. Reached by the redirect in
// ErpProvider while the platform reports a window (config `maintenance` or any
// 503 maintenance answer — lib/maintenance.ts). Return time only when the
// server gave one (store, else ?until=ISO); no countdown, no «قريباً».
//
// The page polls GET /v1/config; when the window has ended it returns to the
// page the person was on (?from=) — auto-resume. «العمل بلا اتصال» enters the
// shell offline (local queue only) for the rest of this window; the shell keeps
// polling and comes back online when the window ends.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CloudOff } from 'lucide-react';
import { Button } from '@/components/Button';
import { Pill } from '@/components/Pill';
import { erpApi, isApiError } from '@/lib/api';
import { sessionStore } from '@/lib/auth';
import { fetchConfig } from '@/lib/config';
import { baghdadParts, formatTime, localDocsLabel } from '@/lib/format';
import { maintenanceStore, useMaintenance } from '@/lib/maintenance';
import { onlineStore } from '@/lib/online';
import { useSyncStatus } from '@/lib/offline-queue';
import { HOME_PATH } from './Forbidden';

let pollMs = 30_000;
/** Test hook: shorten the config poll. */
export function __setMaintenancePollMs(ms: number): void {
  pollMs = ms;
}

/** «٤:٠٠ صباحاً» / «٨:٣٠ مساءً» (the board's spelled-out form, Baghdad time). */
export function returnTimeText(iso: string): string {
  const p = baghdadParts(iso);
  return `${formatTime(iso, { suffix: false })} ${p.hour < 12 ? 'صباحاً' : 'مساءً'}`;
}

/**
 * Poll GET /v1/config (which reports the window to the store — config.ts) now
 * and every `pollMs` while `enabled`. Calls `onEnded` once a successful read
 * says no window is active. A 503 maintenance keeps it on; a network failure
 * proves nothing (keep waiting).
 */
export function useMaintenancePoll(enabled: boolean, onEnded: () => void): void {
  const ended = useRef(onEnded);
  ended.current = onEnded;
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const tick = async () => {
      try {
        const cfg = await fetchConfig();
        if (cfg.maintenance.active) return;
        // The platform says no window; a service may still be the one answering
        // 503 maintenance — ask erp-core too (a normal answer lifts it; no loop).
        if (maintenanceStore.heldByApi() && sessionStore.isAuthenticated()) {
          await erpApi.get('/me', undefined, { authRecovery: false }).catch((e: unknown) => {
            if (isApiError(e) && (e.status === 503 || e.isNetwork)) throw e;
          });
        }
        if (alive && !maintenanceStore.get().active) ended.current();
      } catch {
        // 503 maintenance: api.ts already kept the store on. Network: proves nothing — wait.
      }
    };
    void tick();
    const id = setInterval(() => void tick(), pollMs);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);
}

/** Only same-app paths are followed back (never an absolute URL). */
function safeFrom(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/maintenance')) return HOME_PATH;
  return raw;
}

export function Maintenance() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sync = useSyncStatus();
  const { window: w } = useMaintenance();
  const [leaving, setLeaving] = useState(false);
  const from = safeFrom(params.get('from'));
  const until = w.resumesAt ?? params.get('until');

  // Window over → back to the app (the person's page), online.
  useMaintenancePoll(!leaving, () => {
    setLeaving(true);
    navigate(from, { replace: true });
  });

  const workOffline = () => {
    setLeaving(true);
    // The window may be known only from ?until= (direct link): make it real so
    // the shell stays offline until the poll sees the end.
    if (!maintenanceStore.get().active) maintenanceStore.report({ active: true, resumesAt: until });
    maintenanceStore.workOffline();
    onlineStore.set(false);
    navigate(from, { replace: true });
  };

  return (
    <div className="e-center e-maint" data-testid="maintenance">
      <div className="e-card-auth e-maint-card">
        <span className="e-maint-icon" aria-hidden="true">
          <CloudOff />
        </span>
        <h1 style={{ font: 'var(--t-title)' }}>صيانة مجدولة</h1>
        <p className="muted" data-testid="maintenance-sub">
          {until && <>نعود الساعة {returnTimeText(until)}. </>}
          ما حُرّر على هذا الجهاز محفوظ محلياً وسيُزامَن تلقائياً.
        </p>
        {(sync.pending > 0 || sync.lastSyncAt) && (
          <div className="e-maint-pills">
            {sync.pending > 0 && (
              <Pill tone="warn" data-testid="maintenance-pending">
                {localDocsLabel(sync.pending)} بانتظار المزامنة
              </Pill>
            )}
            {sync.lastSyncAt && <Pill data-testid="maintenance-last-sync">آخر مزامنة {formatTime(sync.lastSyncAt, { suffix: false })}</Pill>}
          </div>
        )}
        <Button variant="line" block onClick={workOffline} data-testid="maintenance-offline">
          العمل بلا اتصال (قراءة + فواتير وسندات)
        </Button>
      </div>
    </div>
  );
}
